// AppView.tsx - Updated with UniversalProvider FullScreen integration
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaHome,
  FaFire,
  FaNewspaper,
  FaBuilding,
  FaBookmark,
  FaSearch,
  FaBell,
  FaUser,
  FaRocket,
  FaFileAlt,
  FaSync,
  FaShieldAlt,
  FaUsers,
  FaHeart,
  FaStar,
  FaClock,
  FaEye,
  FaShareAlt,
  FaBookOpen,
  FaExternalLinkAlt,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaLinkedin,
  FaGavel,
  FaMoneyBillWave,
  FaLeaf,
  FaFutbol,
  FaPalette,
  FaHeartbeat,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaExclamationCircle,
  FaInfoCircle,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaPlus,
  FaLanguage,
  FaArrowUp,
  FaCheckCircle,
  FaFilter,
  FaExpand
} from 'react-icons/fa';
import { ScrollToTop } from './components/ScrollToTop';
import HeaderNews from './appParts/HeaderNews';
import "./AppStyles.css";
import { GazetteAnnouncements } from './pages/GazetteAnnouncements';
import { useUniversal } from './services/UniversalProvider';

// ============================================================
// 1. TYPES & INTERFACES
// ============================================================

interface Author {
  name: string;
  avatar?: string;
  bio?: string;
}

interface NewsItem {
  id: string;
  title: string;
  type: string;
  category: string;
  language: string;
  provider: string;
  sourceUrl: string;
  excerpt: string;
  content: string;
  headerImageURL: string;
  publishedDate: string;
  author: Author;
  tags: string[];
  rating: number;
  views: number;
  ActionButtons: { label: string; url: string; style: string; icon?: string }[];
  socialShare: {
    enabled: boolean;
    platforms: string[];
  };
}

interface NewsProvider {
  id: string;
  uid: string;
  name: string;
  type: string;
  rank: number;
  isTrusted: number;
  isChildSafe: number;
  status: string;
  sourceUrl: string;
  description: string;
  bgImage: string;
  logoImage?: string;
  categories: string[];
  verified: boolean;
  followers: number;
  rating: number;
  articlesCount: number;
  ActionButtons: { label: string; url: string; style: string; icon?: string }[];
}

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  count: number;
  description: string;
}

interface BreakingNews {
  id: string;
  title: string;
  type: string;
  priority: number;
  timestamp: string;
  excerpt: string;
  headerImageURL: string;
  ActionButtons: { label: string; url: string; style: string; icon?: string }[];
}

interface Testimonial {
  id: string;
  name: string;
  avatar: string;
  location: string;
  rating: number;
  comment: string;
  date: string;
}

interface SocialLink {
  platform: string;
  url: string;
  icon: string;
  color: string;
}

interface AppData {
  id: string;
  name: string;
  version: string;
  branding: {
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    favicon: string;
  };
  header: {
    title: string;
    subtitle: string;
    logoImage: string;
    backgroundImage: string;
    backgroundImages: string[];
    tag: string;
    showSearch: boolean;
    showNotifications: boolean;
    navLinks: { label: string; target: string; icon: string; active?: boolean }[];
    userMenu: {
      showLogin: boolean;
      showRegister: boolean;
      showProfile: boolean;
      profileUrl: string;
    };
  };
  hero: {
    title: string;
    subtitle: string;
    logoImage: string;
    backgroundImage: string;
    backgroundImages: string[];
    tag: string;
    overlayOpacity: number;
    buttons: { label: string; url: string; style: string; icon: string }[];
    stats: { label: string; value: string; icon: string }[];
  };
  about: {
    title: string;
    description: string;
    extendedDescription: string;
    stats: { label: string; value: string; icon: string; color: string }[];
    image: string;
    gallery: string[];
    rating: number;
    reviews: number;
    features: { title: string; description: string; icon: string }[];
  };
  map: {
    center: { lat: number; lng: number; name: string };
    zoom: number;
    markers: { id: string; lat: number; lng: number; name: string; type: string; icon: string }[];
    tileLayer: string;
    attribution: string;
  };
  sections: {
    topStories: {
      hashId: string;
      title: string;
      description: string;
      image: string;
      layout: string;
      columns: number;
      items: NewsItem[];
      filters: {
        enabled: boolean;
        categories: string[];
        sort: string[];
      };
      pagination: {
        enabled: boolean;
        itemsPerPage: number;
      };
    };
    categories: {
      hashId: string;
      title: string;
      description: string;
      items: Category[];
      layout: string;
      columns: number;
    };
    popularProviders: {
      hashId: string;
      title: string;
      description: string;
      image: string;
      layout: string;
      columns: number;
      items: NewsProvider[];
      filters: {
        enabled: boolean;
        categories: string[];
        trusted: boolean;
      };
      searchEnabled: boolean;
      pagination: {
        enabled: boolean;
        itemsPerPage: number;
      };
    };
    breakingNews: {
      hashId: string;
      title: string;
      description: string;
      items: BreakingNews[];
      autoRefresh: boolean;
      refreshInterval: number;
    };
  };
  gallery: {
    title: string;
    description: string;
    items: {
      id: string;
      type: string;
      url: string;
      thumbnail: string;
      caption: string;
      category: string;
      featured?: boolean;
    }[];
    categories: string[];
    layout: string;
    lightbox: {
      enabled: boolean;
      showThumbnails: boolean;
      autoplay: boolean;
    };
  };
  testimonials: {
    title: string;
    items: Testimonial[];
    statistics: {
      totalReviews: number;
      averageRating: number;
      activeReaders: number;
    };
  };
  contact: {
    title: string;
    subtitle: string;
    phone: string;
    whatsapp: string;
    email: string;
    website: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      country: string;
      postalCode: string;
    };
    newsTips: {
      enabled: boolean;
      title: string;
      description: string;
      email: string;
    };
    businessHours: {
      [key: string]: string;
    };
    social: SocialLink[];
    contactForm: {
      enabled: boolean;
      fields: {
        name: string;
        label: string;
        type: string;
        required: boolean;
        options?: string[];
      }[];
      submitButton: {
        label: string;
        style: string;
      };
    };
  };
  footer: {
    title: string;
    description: string;
    logoImage: string;
    links: {
      [key: string]: { label: string; url: string }[];
    };
    appDownload: {
      enabled: boolean;
      ios: string;
      android: string;
    };
    newsletter: {
      enabled: boolean;
      title: string;
      description: string;
      placeholder: string;
      buttonLabel: string;
    };
    copyright: string;
    backToTop: boolean;
  };
  features: {
    darkMode: boolean;
    pushNotifications: boolean;
    shareButtons: boolean;
    bookmarks: boolean;
    readingHistory: boolean;
    offlineMode: {
      enabled: boolean;
      cacheArticles: boolean;
      cacheLimit: number;
    };
    languageSelector: {
      enabled: boolean;
      languages: { code: string; name: string; flag: string }[];
    };
    realtimeUpdates: boolean;
    breakingNewsAlerts: boolean;
    commentSystem: {
      enabled: boolean;
      moderation: boolean;
    };
    adSupport: {
      enabled: boolean;
      adProvider: string;
    };
  };
}

// ============================================================
// 2. ICON MAPPER
// ============================================================

const ICON_MAP: Record<string, React.ReactNode> = {
  FaHome: <FaHome />,
  FaFire: <FaFire />,
  FaNewspaper: <FaNewspaper />,
  FaBuilding: <FaBuilding />,
  FaBookmark: <FaBookmark />,
  FaSearch: <FaSearch />,
  FaBell: <FaBell />,
  FaUser: <FaUser />,
  FaRocket: <FaRocket />,
  FaFileAlt: <FaFileAlt />,
  FaSync: <FaSync />,
  FaShieldAlt: <FaShieldAlt />,
  FaUsers: <FaUsers />,
  FaHeart: <FaHeart />,
  FaStar: <FaStar />,
  FaClock: <FaClock />,
  FaEye: <FaEye />,
  FaShareAlt: <FaShareAlt />,
  FaBookOpen: <FaBookOpen />,
  FaExternalLinkAlt: <FaExternalLinkAlt />,
  FaFacebook: <FaFacebook />,
  FaTwitter: <FaTwitter />,
  FaInstagram: <FaInstagram />,
  FaYoutube: <FaYoutube />,
  FaLinkedin: <FaLinkedin />,
  FaGavel: <FaGavel />,
  FaMoneyBillWave: <FaMoneyBillWave />,
  FaLeaf: <FaLeaf />,
  FaFutbol: <FaFutbol />,
  FaPalette: <FaPalette />,
  FaHeartbeat: <FaHeartbeat />,
  FaChevronLeft: <FaChevronLeft />,
  FaChevronRight: <FaChevronRight />,
  FaTimes: <FaTimes />,
  FaExclamationCircle: <FaExclamationCircle />,
  FaInfoCircle: <FaInfoCircle />,
  FaEnvelope: <FaEnvelope />,
  FaPhone: <FaPhone />,
  FaGlobe: <FaGlobe />,
  FaPlus: <FaPlus />,
  FaLanguage: <FaLanguage />,
  FaArrowUp: <FaArrowUp />,
  FaCheckCircle: <FaCheckCircle />,
  FaFilter: <FaFilter />,
  FaExpand: <FaExpand />
};

const CATEGORY_COLORS: Record<string, string> = {
  politics: 'text-red-600 bg-red-100',
  economy: 'text-green-600 bg-green-100',
  environment: 'text-emerald-600 bg-emerald-100',
  education: 'text-blue-600 bg-blue-100',
  health: 'text-rose-600 bg-rose-100',
  sports: 'text-orange-600 bg-orange-100',
  culture: 'text-purple-600 bg-purple-100',
  business: 'text-amber-600 bg-amber-100',
  community: 'text-cyan-600 bg-cyan-100',
  world: 'text-indigo-600 bg-indigo-100'
};

const getIcon = (iconName: string): React.ReactNode =>
  ICON_MAP[iconName] || <FaNewspaper />;

const getCategoryColor = (category: string): string =>
  CATEGORY_COLORS[category] || 'text-gray-600 bg-gray-100';

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ============================================================
// 3. APP VIEW COMPONENT
// ============================================================

interface AppViewProps {
  data?: AppData;
}

export default function AppView({ data }: AppViewProps) {
  // ============ UNIVERSAL HOOK ============
  const { fullscreen, isDarkMode: universalDarkMode, setTheme } = useUniversal();

  // ============ STATE ============
  const [activeTab, setActiveTab] = useState('home');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<NewsItem | null>(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [bookmarkedArticles, setBookmarkedArticles] = useState<string[]>([]);
  const [readingHistory, setReadingHistory] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // ============ DESTRUCTURE DATA ============
  const { 
    header, 
    hero, 
    about, 
    sections, 
    gallery, 
    testimonials, 
    contact, 
    footer,
    features 
  } = data || {};

  // ============ MEMOIZED VALUES ============
  const topStories = sections?.topStories?.items || [];
  const categories = sections?.categories?.items || [];
  const providers = sections?.popularProviders?.items || [];
  const breakingNews = sections?.breakingNews?.items || [];

  const filteredStories = useMemo(() => {
    let stories = topStories;
    
    if (selectedCategory !== 'all') {
      stories = stories.filter(s => s.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      stories = stories.filter(s => 
        s.title.toLowerCase().includes(query) ||
        s.excerpt.toLowerCase().includes(query) ||
        s.provider.toLowerCase().includes(query) ||
        s.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    return stories;
  }, [topStories, selectedCategory, searchQuery]);

  const paginatedStories = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredStories.slice(start, end);
  }, [filteredStories, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredStories.length / itemsPerPage);

  const popularArticles = useMemo(() => {
    return [...topStories]
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  }, [topStories]);

  // ============ HANDLERS ============
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleCategorySelect = useCallback((category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const handleArticleClick = useCallback((article: NewsItem) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
    // Add to reading history
    if (!readingHistory.includes(article.id)) {
      setReadingHistory(prev => [article.id, ...prev].slice(0, 50));
    }
  }, [readingHistory]);

  const handleCloseArticle = useCallback(() => {
    setShowArticleModal(false);
    setTimeout(() => setSelectedArticle(null), 300);
  }, []);

  const toggleBookmark = useCallback((articleId: string) => {
    setBookmarkedArticles(prev => 
      prev.includes(articleId) 
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ============ FULLSCREEN HANDLER ============
  const openGazetteFullscreen = useCallback(() => {
    fullscreen.open(
      <div className="w-full h-full bg-gray-50 dark:bg-gray-900 overflow-auto p-4">
        <GazetteAnnouncements />
      </div>,
      {
        title: 'Gazette Announcements',
        subtitle: 'Official announcements from the Maldives Government Gazette',
        showBackButton: true,
        showCloseButton: true,
        enableZoom: true,
        enableFitToScreen: true,
        enableFullscreenToggle: true,
        enablePrint: false,
        enableDownload: false,
        enableCopy: false,
        enableShare: false,
        enableBookmark: false,
        enableExternalLink: false,
        backgroundColor: 'bg-gray-50 dark:bg-gray-900',
      }
    );
  }, [fullscreen]);

  // ============ EFFECTS ============
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-refresh breaking news
  useEffect(() => {
    if (!sections?.breakingNews?.autoRefresh) return;
    
    const interval = setInterval(() => {
      // In a real app, this would fetch new data
      console.log('Refreshing breaking news...');
    }, sections.breakingNews.refreshInterval || 30000);
    
    return () => clearInterval(interval);
  }, [sections?.breakingNews]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === 'Escape') {
        if (showArticleModal) handleCloseArticle();
        if (showSearch) setShowSearch(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showArticleModal, showSearch, handleCloseArticle]);

  // ============ RENDER HELPERS ============
  const renderSectionHeader = (tag: string, title: string, description: string) => (
    <div className={`text-center mb-12 ${universalDarkMode ? 'text-white' : ''}`}>
      <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
        {tag}
      </span>
      <h2 className={`font-primary text-4xl lg:text-5xl font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'} mb-4`}>
        {title}
      </h2>
      <p className={`font-secondary ${universalDarkMode ? 'text-gray-300' : 'text-gray-500'} max-w-2xl mx-auto`}>
        {description}
      </p>
    </div>
  );

  const renderCategoryBadge = (category: string) => (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(category)}`}>
      {category.charAt(0).toUpperCase() + category.slice(1)}
    </span>
  );

  const renderStarRating = (rating: number) => (
    <div className="flex items-center gap-1">
      <FaStar className="text-yellow-400 text-sm" />
      <span className="font-secondary text-sm font-medium text-gray-600">{rating.toFixed(1)}</span>
    </div>
  );

  // ============ RENDER ============
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-primary">Loading news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${universalDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'} min-h-screen transition-colors duration-300`}>
      <ScrollToTop />

      {/* ===== HEADER ===== */}
      <HeaderNews
        siteName={header?.title || 'Iulaan'}
        logo={header?.logoImage}
        navLinks={header?.navLinks || []}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onSearch={handleSearch}
        onToggleTheme={() => setTheme(universalDarkMode ? 'light' : 'dark')}
        isDarkMode={universalDarkMode}
        showSearch={showSearch}
        onToggleSearch={() => setShowSearch(!showSearch)}
      />

      {/* ===== HERO SECTION ===== */}
      <section id="home" className="relative min-h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${hero?.backgroundImage})`,
            opacity: hero?.overlayOpacity || 0.4
          }}
        />
        <div className="absolute inset-0 bg-black/50" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium tracking-wider">
              {hero?.tag}
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-primary font-bold text-white mb-6">
            {hero?.title}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-secondary leading-relaxed mb-8">
            {hero?.subtitle}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {hero?.buttons.map((button, index) => (
              <a
                key={index}
                href={button.url}
                className={`px-8 py-3 rounded-full font-primary font-semibold transition-all duration-300 flex items-center gap-2 ${
                  button.style === 'primary'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
                    : 'bg-white/20 backdrop-blur-md hover:bg-white/30 text-white border border-white/30'
                }`}
              >
                {getIcon(button.icon)}
                {button.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BREAKING NEWS TICKER ===== */}
      {breakingNews.length > 0 && (
        <div className="bg-red-600 text-white py-2 overflow-hidden">
          <div className="container mx-auto max-w-7xl px-4">
            <div className="flex items-center gap-4 animate-[marquee_20s_linear_infinite]">
              <span className="flex-shrink-0 font-bold text-sm uppercase bg-red-700 px-3 py-1 rounded">
                Breaking
              </span>
              {breakingNews.map((news, index) => (
                <span key={index} className="whitespace-nowrap">
                  {news.title}
                  {index < breakingNews.length - 1 && ' • '}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== MAIN CONTENT ===== */}
      <main className="container mx-auto max-w-7xl px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* ===== LEFT SIDEBAR - Categories ===== */}
          <aside className="lg:col-span-1 space-y-6">
            <div className={`rounded-2xl p-6 ${universalDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg sticky top-24`}>
              <h3 className={`font-primary font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'} mb-4 flex items-center gap-2`}>
                <FaFilter className="text-blue-500" />
                Categories
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleCategorySelect('all')}
                  className={`w-full text-left px-4 py-2 rounded-xl transition-all duration-200 flex items-center justify-between ${
                    selectedCategory === 'all'
                      ? 'bg-blue-500 text-white'
                      : `${universalDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`
                  }`}
                >
                  <span>All News</span>
                  <span className="text-xs opacity-70">{topStories.length}</span>
                </button>
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className={`w-full text-left px-4 py-2 rounded-xl transition-all duration-200 flex items-center justify-between ${
                      selectedCategory === category.id
                        ? 'bg-blue-500 text-white'
                        : `${universalDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {getIcon(category.icon)}
                      {category.name}
                    </span>
                    <span className="text-xs opacity-70">{category.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Articles */}
            <div className={`rounded-2xl p-6 ${universalDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className={`font-primary font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'} mb-4 flex items-center gap-2`}>
                <FaFire className="text-orange-500" />
                Most Popular
              </h3>
              <div className="space-y-4">
                {popularArticles.map(article => (
                  <button
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="w-full text-left group"
                  >
                    <div className="flex gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={article.headerImageURL}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-primary text-sm font-semibold ${universalDarkMode ? 'text-white' : 'text-gray-800'} line-clamp-2 group-hover:text-blue-500 transition-colors`}>
                          {article.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <FaClock />
                          <span>{formatDate(article.publishedDate)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* ===== MAIN CONTENT AREA ===== */}
          <div className="lg:col-span-3">
            {/* Gazette Announcements Section with Fullscreen Button */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className={`font-primary text-2xl font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Gazette Announcements
                  </h2>
                  <p className={`font-secondary text-sm ${universalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Official announcements from the Maldives Government Gazette
                  </p>
                </div>
                <button
                  onClick={openGazetteFullscreen}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl flex items-center gap-2 transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  <FaExpand size={16} />
                  <span className="hidden sm:inline">Fullscreen</span>
                </button>
              </div>
              
              {/* Gazette Announcements Component */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden p-4">
                <GazetteAnnouncements />
              </div>
            </div>

            {/* Top Stories */}
            <section id="topstories">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className={`font-primary text-2xl font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {sections?.topStories?.title || 'Top Stories'}
                  </h2>
                  <p className={`font-secondary text-sm ${universalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {sections?.topStories?.description || 'Latest news from across the Maldives'}
                  </p>
                </div>
                <span className={`font-secondary text-sm ${universalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {filteredStories.length} articles
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {paginatedStories.map((article, index) => (
                  <article
                    key={article.id}
                    className={`group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ${
                      universalDarkMode ? 'bg-gray-800' : 'bg-white'
                    }`}
                  >
                    <div className="relative overflow-hidden h-48 cursor-pointer">
                      <img
                        src={article.headerImageURL}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onClick={() => handleArticleClick(article)}
                      />
                      <div className="absolute top-3 left-3">
                        {renderCategoryBadge(article.category)}
                      </div>
                      <button
                        onClick={() => toggleBookmark(article.id)}
                        className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${
                          bookmarkedArticles.includes(article.id)
                            ? 'bg-blue-500 text-white'
                            : 'bg-black/50 text-white hover:bg-black/70'
                        }`}
                      >
                        <FaBookmark className="text-sm" />
                      </button>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-secondary text-xs text-gray-500">
                          {article.provider}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" />
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <FaClock />
                          <span>{formatDate(article.publishedDate)}</span>
                        </div>
                      </div>
                      <h3 
                        className={`font-primary text-xl font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'} mb-2 line-clamp-2 cursor-pointer hover:text-blue-500 transition-colors`}
                        onClick={() => handleArticleClick(article)}
                      >
                        {article.title}
                      </h3>
                      <p className={`font-secondary text-sm ${universalDarkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2 mb-3`}>
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <FaEye />
                            {article.views.toLocaleString()}
                          </span>
                          {renderStarRating(article.rating)}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleArticleClick(article)}
                            className="text-blue-500 hover:text-blue-600 font-semibold text-sm flex items-center gap-1"
                          >
                            Read More
                            <FaChevronRight className="text-xs" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-xl transition-all duration-200 ${
                      currentPage === 1
                        ? 'opacity-50 cursor-not-allowed'
                        : `${universalDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`
                    }`}
                  >
                    <FaChevronLeft />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-10 h-10 rounded-xl transition-all duration-200 ${
                        page === currentPage
                          ? 'bg-blue-500 text-white'
                          : `${universalDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-xl transition-all duration-200 ${
                      currentPage === totalPages
                        ? 'opacity-50 cursor-not-allowed'
                        : `${universalDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`
                    }`}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {/* ===== ARTICLE DETAIL MODAL ===== */}
      {selectedArticle && (
        <div
          className={`fixed inset-0 z-50 overflow-y-auto ${showArticleModal ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300`}
          onClick={handleCloseArticle}
        >
          <div className="min-h-screen bg-black/80 backdrop-blur-sm p-4 flex items-center justify-center">
            <div
              className={`${universalDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-transform duration-300 ${
                showArticleModal ? 'scale-100' : 'scale-95'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 bg-inherit p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <button
                  onClick={handleCloseArticle}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <FaTimes className={universalDarkMode ? 'text-white' : 'text-gray-700'} />
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleBookmark(selectedArticle.id)}
                    className={`p-2 rounded-full transition-colors ${
                      bookmarkedArticles.includes(selectedArticle.id)
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500'
                    }`}
                  >
                    <FaBookmark />
                  </button>
                  <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500">
                    <FaShareAlt />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <img
                  src={selectedArticle.headerImageURL}
                  alt={selectedArticle.title}
                  className="w-full h-[300px] object-cover rounded-2xl mb-6"
                />
                <div className="flex items-center gap-3 mb-4">
                  {renderCategoryBadge(selectedArticle.category)}
                  <span className="font-secondary text-sm text-gray-500">
                    {selectedArticle.provider}
                  </span>
                  <span className="font-secondary text-sm text-gray-400">
                    {formatDate(selectedArticle.publishedDate)}
                  </span>
                </div>
                <h1 className={`font-primary text-3xl lg:text-4xl font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'} mb-4`}>
                  {selectedArticle.title}
                </h1>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <img
                      src={selectedArticle.author.avatar || 'https://ui-avatars.com/api/?name=' + selectedArticle.author.name}
                      alt={selectedArticle.author.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <p className={`font-primary font-semibold ${universalDarkMode ? 'text-white' : 'text-gray-800'} text-sm`}>
                        {selectedArticle.author.name}
                      </p>
                      <p className="font-secondary text-xs text-gray-500">
                        {selectedArticle.author.bio}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={`font-secondary ${universalDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed space-y-4`}>
                  <p className="text-lg font-medium">{selectedArticle.excerpt}</p>
                  <div dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
                </div>
                <div className="flex flex-wrap gap-2 mt-6">
                  {selectedArticle.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-sm text-gray-600 dark:text-gray-400">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ABOUT SECTION ===== */}
      <section id="about" className={`py-20 ${universalDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto max-w-7xl px-4">
          {renderSectionHeader(
            'About',
            about?.title || 'About Iulaan',
            about?.description || ''
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className={`font-secondary ${universalDarkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed mb-6`}>
                {about?.extendedDescription}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {about?.stats.map((stat, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className={`text-xl ${stat.color}`}>
                      {getIcon(stat.icon)}
                    </span>
                    <div>
                      <p className={`font-primary font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {stat.value}
                      </p>
                      <p className={`font-secondary text-xs ${universalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {stat.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={about?.image}
                alt="About"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 dark:bg-green-900 rounded-full p-3">
                    <FaStar className="text-yellow-500 text-2xl" />
                  </div>
                  <div>
                    <p className={`font-primary font-bold text-2xl ${universalDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {about?.rating}
                    </p>
                    <p className={`font-secondary text-sm ${universalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {about?.reviews}+ reviews
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className={`py-20 ${universalDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto max-w-7xl px-4">
          {renderSectionHeader(
            'Testimonials',
            testimonials?.title || 'What Our Readers Say',
            'Hear from our community of readers'
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials?.items.map((testimonial) => (
              <div key={testimonial.id} className={`rounded-2xl p-6 shadow-lg ${universalDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <p className={`font-primary font-bold ${universalDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {testimonial.name}
                    </p>
                    <p className="font-secondary text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className={i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'} />
                  ))}
                </div>
                <p className={`font-secondary ${universalDarkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                  "{testimonial.comment}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className={`${universalDarkMode ? 'bg-gray-900 border-t border-gray-800' : 'bg-gray-900'} text-white py-16`}>
        <div className="container mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-primary text-2xl font-bold mb-4">{footer?.title}</h3>
              <p className="font-secondary text-gray-400 leading-relaxed">
                {footer?.description}
              </p>
              {footer?.appDownload?.enabled && (
                <div className="flex gap-3 mt-4">
                  <a href={footer.appDownload.ios} className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition-colors">
                    App Store
                  </a>
                  <a href={footer.appDownload.android} className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition-colors">
                    Play Store
                  </a>
                </div>
              )}
            </div>
            {Object.entries(footer?.links || {}).map(([title, links]) => (
              <div key={title}>
                <h4 className="font-primary font-bold text-lg mb-4">{title}</h4>
                <ul className="space-y-2">
                  {links.map((link, index) => (
                    <li key={index}>
                      <a href={link.url} className="font-secondary text-gray-400 hover:text-white transition-colors">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          {footer?.newsletter?.enabled && (
            <div className="border-t border-gray-800 mt-8 pt-8">
              <div className="max-w-md mx-auto text-center">
                <h4 className="font-primary font-bold text-lg mb-2">{footer.newsletter.title}</h4>
                <p className="font-secondary text-gray-400 text-sm mb-4">{footer.newsletter.description}</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder={footer.newsletter.placeholder}
                    className="flex-1 px-4 py-2 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  />
                  <button className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold transition-colors">
                    {footer.newsletter.buttonLabel}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-secondary text-sm text-gray-400">{footer?.copyright}</p>
            <div className="flex gap-4">
              {contact?.social.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  className={`w-10 h-10 rounded-full ${social.color} hover:opacity-80 transition-opacity flex items-center justify-center text-white`}
                >
                  {getIcon(social.icon)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* ===== BACK TO TOP ===== */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 z-50"
        >
          <FaArrowUp />
        </button>
      )}
    </div>
  );
}